import { CoinIcon, CouponIcon, LoaderIcon, LoyaltyLockIcon, LoyaltyPromoAndDiscountIcon, VoucherListIcon } from '../../../utils/svg';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatPoint, getCssVar } from '../../../utils/helper';
import ImageLoader from '../ImageLoader';
import { useMemo, useState } from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { FormProfile } from '../Profile/Form';
import { useSelector } from 'react-redux';
import useNavigation from '../../../hook/useNavigation';
import Iframe from '../Iframe';
import { UFOODX_BASE_URL, grey } from '../../../utils/constants';

const Unlocked = (props) => {
	const { tokenProfile, loyalty, isLoading, tierConfig, loyaltyVouchers } = props;
	const { companyLoyaltyWidgetUrl } = useSelector((store) => store?.company);
	const [isShowBottomSheet, setIsShowBottomSheet] = useState(false);
	const navigate = useNavigation();

	const tier = useMemo(() => {
		return tierConfig?.tiers?.find((t) => t?._id === loyalty?.tierId);
	}, [tierConfig?.tiers, loyalty?.tierId]);

	const isPointOnly = false;

	const handleCloseBottomSheet = () => {
		setIsShowBottomSheet(false);
	};

	const handleClick = () => {
		navigate.push(
			`${UFOODX_BASE_URL}/loyalty?widgetUrl=${companyLoyaltyWidgetUrl}&accessToken=${tokenProfile}&clientId=${loyalty?._id}`
		);
	};

	const iframeProps = {
		handleCloseBottomSheet,
		src: `${companyLoyaltyWidgetUrl}/login?accessToken=${tokenProfile}&clientId=${loyalty?._id}`,
	};

	return (
		<>
			<div className='bg-white flex items-center rounded-full px-2 py-0.5 ml-2' onClick={handleClick}>
				{isLoading ? (
					<div className='w-16 h-5 flex justify-center items-center px-2'>
						<LoaderIcon />
					</div>
				) : (
					<>
						<ImageLoader src={tier?.image || loyalty?.pointLogo} alt={tier?.name || loyalty?.tier} className='h-5 w-auto' />
						<div className={`p-1 text-xxs text-[var(--loyalty-font-color)] font-semibold`}>{tier?.name || loyalty?.tier} Tier</div>
					</>
				)}
			</div>
			<div className='flex justify-end items-center' onClick={handleClick}>
				{isLoading ? (
					<div className='w-16 h-5 flex justify-center items-center px-2'>
						<LoaderIcon />
					</div>
				) : isPointOnly ? (
					<>
						<div className='flex gap-1 text-xxs text-white font-semibold items-center'>
							<CoinIcon className={'h-3.5 w-auto'} /> {formatPoint(loyalty?.point)} Point
						</div>
						<div className='flex items-center mx-4'>
							<ChevronRightIcon className='h-3 sm:h-4 w-auto text-white' strokeWidth={3} />
						</div>
					</>
				) : (
					<>
						<div className='flex gap-2 items-center'>
							<div>
								<div className='text-xxs text-white font-thin'>Point</div>
								<div className='text-xxs text-white font-semibold flex gap-1'>
									<CoinIcon className={'h-3.5 w-auto'} />
									<div className='line-clamp-1 w-8 xxs:w-full'>{formatPoint(loyalty?.point || 0)}</div>
								</div>
							</div>
							<div>
								<div className='text-xxs text-white font-thin'>Voucher</div>
								<div className='text-xxs text-white font-semibold flex gap-1'>
									<CouponIcon className={'h-3.5 w-auto'} />
									{loyaltyVouchers?.totalDocs || 0}
								</div>
							</div>
							<div className='flex items-center mx-4'>
								<ChevronRightIcon className='h-3 sm:h-4 w-auto text-white' strokeWidth={3} />
							</div>
						</div>
					</>
				)}
			</div>
			<BottomSheet
				key={'loyalty=iframe'}
				open={isShowBottomSheet}
				header={false}
				snapPoints={({ maxHeight }) => {
					return [maxHeight];
				}}>
				<Iframe {...iframeProps} />
			</BottomSheet>
		</>
	);
};

const Locked = (props) => {
	const { isLoading } = props;
	return (
		<>
			<div className='flex items-center rounded-full ml-2'>
				{isLoading ? (
					<div className='w-20 h-5 flex justify-center items-center px-2 py-3 gap-1'>
						<LoaderIcon />
					</div>
				) : (
					<>
						<div className='flex gap-1 text-xxs text-white font-thin items-center px-2 py-2'>
							<LoyaltyPromoAndDiscountIcon className='h-6 w-auto pr-1' />
							Get more points and voucher, <div className='font-semibold'>Click here!!</div>
						</div>
					</>
				)}
			</div>
			<div className='flex items-center border-l-1 border-gray-300 border-solid h-full'>
				<div className='h-full w-auto flex justify-center items-center rounded-tr-md mx-4 gap-1'>
					{isLoading ? (
						<LoaderIcon />
					) : (
						<>
							<LoyaltyLockIcon className='h-5 w-auto' />
							<ChevronRightIcon className='h-3 sm:h-4 w-auto text-white' strokeWidth={3} />
						</>
					)}
				</div>
			</div>
		</>
	);
};

const Loyality = (props) => {
	const { loyalty, className, isProfileCompleted, clientConfig, isLogin, hidden } = props;
	const [isShowBottomSheet, setIsShowBottomSheet] = useState(false);
	const navigate = useNavigation();

	const isActive = isProfileCompleted && loyalty?.status === 'active';

	const handleCloseBottomSheet = () => {
		setIsShowBottomSheet(false);
	};

	const handleClick = async () => {
		if (!isLogin) {
			navigate.push('/ufood-x/login');
			return;
		}
		if (isActive) return;
		setIsShowBottomSheet(true);
	};

	const bgColor = useMemo(() => {
		const defaultBgColor = clientConfig?.settings?.find((s) => s?.name === 'primaryColor')?.value || grey;
		return getCssVar('--loyalty-bg-color', defaultBgColor);
	}, [clientConfig?.settings]);

	return (
		<>
			<div style={{ '--loyalty-bg-color': bgColor }} className={`flex h-12 items-center justify-between ${className} rounded-t-md bg-gradient-to-r bg-[var(--loyalty-bg-color)] ${hidden && 'hidden'}`} onClick={handleClick}>
				{isActive ? <Unlocked {...props} /> : <Locked {...props} />}
			</div>
			<BottomSheet
				key={'form-my-profile'}
				open={isShowBottomSheet}
				onDismiss={handleCloseBottomSheet}
				snapPoints={({ minHeight, maxHeight }) => {
					return [minHeight, maxHeight];
				}}>
				<FormProfile onClose={handleCloseBottomSheet} />
			</BottomSheet>
		</>
	);
};

export default Loyality;
